import { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router';
import { useDebounce } from '@/hooks/use-debounce';
import type { ReadingListStatus } from '@/types';

// Filtros de "Lecturas Asignadas" sincronizados con la URL (UX-NAV-02): pestaña
// (?status), búsqueda (?search) y página (?page). La URL es la fuente de verdad, así un
// enlace o un "atrás" reproduce la vista. Conserva los demás parámetros de la ruta
// (?highlight / ?confirm de los deep-links de correo).
const STATUSES: readonly ReadingListStatus[] = ['pending', 'completed', 'overdue'];

function parseStatus(raw: string | null): ReadingListStatus | undefined {
  return STATUSES.find((s) => s === raw);
}

export function useReadingFilters() {
  const [searchParams, setSearchParams] = useSearchParams();
  const status = parseStatus(searchParams.get('status'));
  const urlSearch = searchParams.get('search') ?? '';
  const page = Math.max(1, Number(searchParams.get('page')) || 1);

  // El input se edita localmente y llega a la URL con debounce (no un request por tecla).
  const [searchInput, setSearchInput] = useState(urlSearch);
  const debounced = useDebounce(searchInput, 300);

  // Si la URL cambia desde afuera (sidebar, notificación, atrás) sin remontar la página,
  // el input se alinea con ella: la URL es la fuente de verdad. Ajuste en render (no en
  // un efecto). Si el cambio lo originó el propio input (debounce), ya coinciden.
  const [syncedUrlSearch, setSyncedUrlSearch] = useState(urlSearch);
  if (urlSearch !== syncedUrlSearch) {
    setSyncedUrlSearch(urlSearch);
    if (urlSearch !== searchInput.trim()) setSearchInput(urlSearch);
  }

  function update(mutate: (next: URLSearchParams) => void) {
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        mutate(next);
        return next;
      },
      { replace: true },
    );
  }

  useEffect(() => {
    const term = debounced.trim();
    if (term === urlSearch) return;
    update((next) => {
      if (term) next.set('search', term);
      else next.delete('search');
      next.delete('page'); // una búsqueda nueva vuelve a la primera página
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debounced]);

  return {
    status,
    search: urlSearch,
    searchInput,
    page,
    hasFilters: Boolean(status || urlSearch),
    setSearchInput,
    setStatus(value: ReadingListStatus | undefined) {
      update((next) => {
        if (value) next.set('status', value);
        else next.delete('status');
        next.delete('page');
      });
    },
    setPage(value: number) {
      update((next) => {
        if (value > 1) next.set('page', String(value));
        else next.delete('page');
      });
    },
    clear() {
      setSearchInput('');
      update((next) => {
        next.delete('status');
        next.delete('search');
        next.delete('page');
      });
    },
  };
}
